<template>
  <div class="uk-container">
    <div class="uk-card uk-card-default block-query">
      <div class="query">
        <div class="section">
          <div class="item">
            <div class="label">
              <div class="part small">Customer</div>
              <div class="part">Name</div>
            </div>

            <div class="element composite">
              <input v-model="query.customer.name.first" class="uk-input uk-form-small query-input" type="text" placeholder="First Name">
              <input v-model="query.customer.name.last" class="uk-input uk-form-small query-input" type="text" placeholder="Last Name">
            </div>
          </div>

          <div class="item">
            <div class="label">
              <div class="part small">Customer</div>
              <div class="part">Phone</div>
            </div>

            <div class="element">
              <input v-model="query.customer.phone" class="uk-input uk-form-small query-input" type="text">
            </div>
          </div>

          <div class="item">
            <div class="label">
              <div class="part small">Customer</div>
              <div class="part">Email</div>
            </div>

            <div class="element">
              <input v-model="query.customer.email" class="uk-input uk-form-small query-input" type="text">
            </div>
          </div>
        </div>

        <div class="section">
          <div class="item">
            <div class="label">
              <div class="part small">Appointment</div>
              <div class="part">Date</div>
            </div>

            <div class="element">
              <v-date-picker v-model="query.appointment.date" class="date-select" />
            </div>
          </div>

          <div v-if="!isDentist" class="item">
            <div class="label">
              <div class="part small">Appointment</div>
              <div class="part">Practice</div>
            </div>

            <div class="element">
              <multiselect v-model="query.appointment.dentist" class="filter-dentist multiselect-small" placeholder="Search by Dentist" label="name" track-by="id" :options="optionsDentists" :multiple="false" :searchable="true" :hide-selected="true" select-label="" />
            </div>
          </div>

          <div class="item">
            <div class="label">
              <div class="part small">Appointment</div>
              <div class="part">ID</div>
            </div>

            <div class="element">
              <input v-model="query.appointment.id" class="uk-input uk-form-small query-input" type="text">
            </div>
          </div>
        </div>
      </div>

      <div class="actions">
        <div class="action">
          <button class="uk-button uk-button-secondary uk-button-small" @click="clearQuery">Clear Query</button>
        </div>
        <div class="action">
          <button class="uk-button uk-button-primary uk-button-small" :disabled="!checkQuery" @click="submitQuery">Search</button>
        </div>
      </div>
    </div>

    <div class="uk-card uk-card-default table">
      <vue-good-table
        :columns="columns"
        :rows="rows"
        :sort-options="{ initialSortBy: {field: 'appointmentTimestamp', type: 'desc'} }"
        :pagination-options="$globals.vgt.pagination"
        :row-style-class="rowStyleClassFn"
        style-class="uk-table uk-table-middle uk-table-divider uk-margin-remove datatable dt-appointments"
        @on-row-click="onRowClick"
      >
        <template slot="table-column" slot-scope="props">
          <span v-html="formatLabel(props.column.compositeLabel, props.column.label)" />
        </template>

        <template slot="table-row" slot-scope="props">
          <template v-if="props.column.field === 'appointmentTimestamp'">
            <div :uk-tooltip="`title: ${$moment(props.row.appointmentTimestamp, 'X').fromNow()}`">
              <p>{{ $moment(props.row.appointmentTimestamp, 'X').format('DD/MM/YYYY') }}</p>
              <p>{{ $moment(props.row.appointmentTimestamp, 'X').format('HH:mm:ss') }}</p>
            </div>
          </template>

          <template v-else-if="props.column.field === 'appointmentDate'">
            <div :uk-tooltip="`title: ${$helpers.timeBits.find(b => b.id === props.row.appointmentDate.bit).time.long}`">
              <p>{{ $moment(props.row.appointmentDate.date, 'YYMMDD').format('DD/MM/YYYY') }}</p>
              <p>{{ $helpers.timeBits.find(b => b.id === props.row.appointmentDate.bit).time.short }}</p>
            </div>
          </template>

          <template v-else-if="props.column.field === 'appointmentStatus'">
            <span v-if="props.formattedRow[props.column.field] === 0" class="uk-label uk-label-warning">Incomplete</span>
            <span v-else-if="props.formattedRow[props.column.field] === 1" class="uk-label uk-label-success">Booked</span>
            <span v-else-if="props.formattedRow[props.column.field] === 2" class="uk-label uk-label-danger">Cancelled</span>
            <span v-else class="uk-label uk-label-danger">Error</span>
          </template>

          <template v-else-if="props.column.field === 'paymentStatus'">
            <span v-if="props.formattedRow[props.column.field] === 'succeeded'" class="uk-label uk-label-success">Success</span>
            <span v-else-if="props.formattedRow[props.column.field] === 'requires_payment_method'" class="uk-label uk-label-warning">Incomplete</span>
            <span v-else class="uk-label uk-label-danger">Error</span>
          </template>

          <template v-else>
            {{ props.formattedRow[props.column.field] }}
          </template>
        </template>

        <div slot="emptystate">
          <p>No appointments match the search query!</p>
        </div>
      </vue-good-table>
    </div>
  </div>
</template>

<script>
import nowMixin from '@/utils/mixins/now'

export default {
  name: 'ViewAppointmentQuery',

  mixins: [nowMixin],

  data() {
    return {
      query: {
        customer: {
          name: {
            first: null,
            last: null
          },
          phone: null,
          email: null
        },
        appointment: {
          date: null,
          dentist: null,
          id: null
        }
      }
    }
  },

  computed: {
    checkQuery() {
      return !this.checkObject(this.query)
    },

    rows() {
      const source = this.$store.state.appointments.query
      const response = []

      if (source) {
        source.forEach((item) => {
          response.push({
            id: item.id,
            name: `${item.customer.name.first} ${item.customer.name.last}`,
            phone: item.customer.contact.phone,
            email: item.customer.contact.email,
            appointmentTimestamp: item.info.created.timestamp.seconds,
            appointmentDate: !this.isDentist ? item.dentist.data[item.info.dentist] : item.dentist.data[this.$store.state.account.profile.dentist],
            appointmentStatus: !this.isDentist ? item.info.status : item.dentist.data[this.$store.state.account.profile.dentist].status,
            dentist: !this.isDentist ? this.getDentist(item.info.dentist) : '',
            operator: !this.isDentist ? this.getOperator(item.info.created.user) : '',
            paymentStatus: !this.isDentist && item.payment ? item.payment.status : '',
            read: this.isDentist ? item.dentist.data[this.$store.state.account.profile.dentist].read : item.dentist.data[item.info.dentist].read
          })
        })
      }

      // if (this.filters.dentist) {
      //   response = response.filter((item) => item.dentist === this.filters.dentist.name)
      // }

      // if (this.filters.date) {
      //   response = response.filter((item) => item.appointmentDate.date === this.filters.date.value)
      // }

      return response.reverse()
    },

    columns() {
      const columns = {
        appointmentId: {
          hidden: true,
          field: 'id'
        },
        customerName: {
          compositeLabel: true,
          label: [
            {
              value: 'Customer',
              small: true
            },
            {
              value: 'Name',
              small: false
            }
          ],
          field: 'name'
        },
        customerPhone: {
          compositeLabel: true,
          label: [
            {
              value: 'Customer',
              small: true
            },
            {
              value: 'Phone',
              small: false
            }
          ],
          field: 'phone',
          sortable: false
        },
        customerEmail: {
          compositeLabel: true,
          label: [
            {
              value: 'Customer',
              small: true
            },
            {
              value: 'Email',
              small: false
            }
          ],
          field: 'email',
          sortable: false
        },
        appointmentTimestamp: {
          compositeLabel: true,
          label: [
            {
              value: 'Appointment',
              small: true
            },
            {
              value: 'Created',
              small: false
            }
          ],
          field: 'appointmentTimestamp',
          type: 'number'
        },
        appointmentDate: {
          compositeLabel: true,
          label: [
            {
              value: 'Appointment',
              small: true
            },
            {
              value: 'Date',
              small: false
            }
          ],
          field: 'appointmentDate',
          type: 'number',
          sortFn: this.sortAppointmentDate
        },
        appointmentStatus: {
          compositeLabel: true,
          label: [
            {
              value: 'Appointment',
              small: true
            },
            {
              value: 'Status',
              small: false
            }
          ],
          field: 'appointmentStatus',
          type: 'number'
        },
        dentist: {
          compositeLabel: false,
          label: 'Dentist',
          field: 'dentist'
        },
        operator: {
          compositeLabel: false,
          label: 'Operator',
          field: 'operator'
        },
        paymentStatus: {
          compositeLabel: true,
          label: [
            {
              value: 'Payment',
              small: false
            },
            {
              value: 'Status',
              small: true
            }
          ],
          field: 'paymentStatus'
        }
      }

      if (this.isDentist) {
        delete columns.dentist
        delete columns.operator
        delete columns.paymentStatus
      }

      // Convert to Array
      return Object.values(columns)
    },

    isDentist() {
      return this.$store.getters['account/type'] === 'dentist'
    },

    optionsDentists() {
      let response = null

      if (!this.isDentist) {
        const source = this.$store.state.dentists.collection
        response = source.map(({ id, name }) => ({ id, name }))
      }

      return response
    }
  },

  methods: {
    checkObject(object) {
      return Object.values(object).every((v) => (v && typeof v === 'object'
        ? this.checkObject(v)
        : v === 0 || v === null))
    },

    getDentist(i) {
      const dentist = i ? this.$store.getters['dentists/find'](i) : null
      return dentist ? dentist.name : 'N/A'
    },

    getOperator(i) {
      const operator = i ? this.$store.getters['users/find'](i) : null
      return operator ? `${operator.name.first} ${operator.name.last}` : 'N/A'
    },

    formatLabel(composite, data) {
      let response = null

      if (composite) {
        response = `
          <div class="label">
            <span class="${(data[0].small) ? 'small' : ''}">${data[0].value}</span><br>
            <span class="${(data[1].small) ? 'small' : ''}">${data[1].value}</span>
          </div>
        `
      } else {
        response = data
      }

      return response
    },

    onRowClick(params) {
      this.$router.push({ name: 'appointment-single', params: { id: params.row.id } })
    },

    sortAppointmentDate(sx, sy) {
      const x = sx.d.concat(sx.b)
      const y = sy.d.concat(sy.b)
      let response = null

      if (x < y) {
        response = -1
      } else if (x > y) {
        response = 1
      } else {
        response = 0
      }

      return response
    },

    rowStyleClassFn(row) {
      let rowClass = null

      if (this.isDentist) {
        rowClass = row.read ? '' : 'new'
      }

      return rowClass
    },

    cleanInput(input) {
      return input.toLowerCase().replace(/\s/g, '')
    },

    async submitQuery() {
      if (!this.checkQuery) {
        this.$uikit.notification('Please input some query parameters!', { status: 'danger' })
        return
      }

      // Create a reference to the appointments collection
      const appointmentsRef = this.$firebase.firestore().collection(this.$globals.firebase.collection.appointments)

      // Create a query against the collection
      try {
        let query = appointmentsRef

        if (this.query.customer.name.first) {
          query = query.where('info.search.customer.fname', '==', this.cleanInput(this.query.customer.name.first))
        }

        if (this.query.customer.name.last) {
          query = query.where('info.search.customer.lname', '==', this.cleanInput(this.query.customer.name.last))
        }

        if (this.query.customer.phone) {
          const phone = this.$parsePhone(this.query.customer.phone || '', 'GB').number
          console.log(phone)
          query = query.where('customer.contact.phone', '==', phone)
        }

        if (this.query.customer.email) {
          query = query.where('customer.contact.email', '==', this.query.customer.email)
        }

        if (this.query.appointment.date) {
          const date = this.$moment(this.query.appointment.date).format('YYMMDD')
          query = query.where('info.search.appointment.date', '==', date)
        }

        if (!this.isDentist && this.query.appointment.dentist) {
          query = query.where('info.dentist', '==', this.query.appointment.dentist.id)
        }

        if (this.query.appointment.id) {
          // Ignore other fields because only one doc can match Appointment ID
          query = appointmentsRef.where(this.$firebase.firestore.FieldPath.documentId(), '==', this.query.appointment.id)
        }

        // If dentist - only return appropriate results - Only return appointments that are connected to the practice
        if (this.isDentist) {
          query = query.where('dentist.list', 'array-contains', this.$store.state.account.profile.dentist)
        }

        const result = await query.get()
        this.processResult(result.docs)
      } catch (err) {
        console.log('Error getting documents', err)
      }
    },

    processResult(result) {
      const response = []

      result.forEach((item) => {
        response.push({ ...item.data(), id: item.id })
      })

      this.$store.commit('appointments/setQuery', response)
    },

    clearQuery() {
      this.query = {
        customer: {
          name: {
            first: null,
            last: null
          },
          phone: null,
          email: null
        },
        appointment: {
          date: null,
          practice: null,
          id: null
        }
      }

      this.$store.commit('appointments/setQuery', null)
    }
  }
}
</script>

<style lang="scss" scoped>
  .block-query {
    margin-bottom: 1em;
    padding: 1em;

    .query {
      margin-bottom: 1em;

      .section {
        display: flex;
        flex-wrap: wrap;

        .item {
          flex: 1 1 0%;
          min-width: 300px;
          margin: 0 .5em 1em .5em;

          .label {
            .part {
              display: inline-block;
              font-weight: 500;

              &:not(:last-of-type) {
                margin-right: 4px;
              }

              &.small {
                font-weight: 300;
              }
            }
          }

          .element {
            &.composite {
              display: flex;

              .query-input:not(:last-of-type) {
                margin-right: .5em;
              }
            }
          }
        }

      }
    }

    .actions {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;

      .action {
        flex: none;

        &:not(:last-of-type) {
          margin-right: .5em;
        }
      }
    }
  }
</style>

<style lang="scss">
  .date-select {
    input {
      box-sizing: border-box;
      height: 30px;
      padding: 0 8px 0 8px;
      border: 1px solid #e5e5e5;
      border-radius: 0;
      color: #666;
      font-size: 0.875rem;

      &:focus {
        border-color: $colour-primary;
        box-shadow: none;
      }
    }
  }
</style>
